import { text } from './content';

/**
 * Authored NPC dialogue, not live player activity or generated conversation.
 * Keep the existing NPC name/role label visible when displaying these lines.
 * Dialogue stages provide tone; they do not grant rewards or unlock privileges.
 */
export const npcStoryCopy = {
  maya: {
    intro: text(
      "I'm Maya, the café's NPC manager. Coffee on the counter, subway rumble outside. Ready to try a shift?",
      '我是 Maya，咖啡馆的 NPC 经理。柜台上飘着咖啡香，街外传来地铁的低鸣。想试着上一班吗？',
      '我是 Maya，咖啡館的 NPC 經理。櫃檯上飄著咖啡香，街外傳來地鐵的低鳴。想試著上一班嗎？',
      'Soy Maya, la gerente NPC del café. Huele a café y afuera retumba el metro. ¿Te animas a un turno?',
    ),
    progress: text(
      'One order at a time. The street can rush all it likes; you have a little space to get the next cup right.',
      '一单一单来。街上的人尽管赶路，你可以在这里稳稳做好下一杯。',
      '一單一單來。街上的人儘管趕路，你可以在這裡穩穩做好下一杯。',
      'Un pedido a la vez. Que la calle vaya con prisa; aquí tienes un momento para preparar bien la próxima taza.',
    ),
    familiar: text(
      'Good to see you back at this end of the counter. The morning smells like coffee again. That part never gets old.',
      '又在柜台这头见到你了，真好。早晨还是这个咖啡味，怎么闻都不腻。',
      '又在櫃檯這頭見到你了，真好。早晨還是這個咖啡味，怎麼聞都不膩。',
      'Qué gusto verte otra vez a este lado del mostrador. La mañana vuelve a oler a café. De eso no me canso.',
    ),
  },
  sam: {
    intro: text(
      'Sam here, your NPC neighborhood grocer. Fruit crates out front, sandwiches inside. Welcome to this little corner of Brooklyn.',
      '我是 Sam，街区杂货铺的 NPC 店员。水果摆在门外，三明治在店里。欢迎来到布鲁克林的这个小街角。',
      '我是 Sam，街區雜貨鋪的 NPC 店員。水果擺在門外，三明治在店裡。歡迎來到布魯克林的這個小街角。',
      'Soy Sam, el tendero NPC del barrio. Fruta en la entrada, sándwiches adentro. Te doy la bienvenida a este rinconcito de Brooklyn.',
    ),
    progress: text(
      'A sandwich between shifts is a fine plan. Not every meal in New York has to be an occasion.',
      '两班之间吃份三明治，就挺好。在纽约，也不用每顿饭都吃得郑重其事。',
      '兩班之間吃份三明治，就挺好。在紐約，也不用每頓飯都吃得鄭重其事。',
      'Un sándwich entre turnos suena bien. En Nueva York tampoco hace falta convertir cada comida en un gran plan.',
    ),
    familiar: text(
      "There's that familiar face. A corner shop is a small thing, but it's nice to have one you can keep coming back to.",
      '熟面孔又来了。一间街角小店不算什么大事，不过有个随时能回来的地方，感觉不错吧。',
      '熟面孔又來了。一間街角小店不算什麼大事，不過有個隨時能回來的地方，感覺不錯吧。',
      'Mira quién ha vuelto. Una tienda de barrio es poca cosa, pero da gusto tener una a la que regresar.',
    ),
  },
  alex: {
    intro: text(
      "I'm Alex, the NPC career guide. Let's look at your next step. The rest of your life doesn't need an answer this morning.",
      '我是 Alex，这里的 NPC 职业顾问。先看看下一步怎么走，剩下的人生不用今天早上就想明白。',
      '我是 Alex，這裡的 NPC 職業顧問。先看看下一步怎麼走，剩下的人生不用今天早上就想明白。',
      'Soy Alex, tu guía laboral NPC. Veamos el siguiente paso. El resto de tu vida no necesita una respuesta esta mañana.',
    ),
    progress: text(
      'A bit of practice, then a shift to try it out. You can explore a different line of work without erasing what you have learned.',
      '先练一点，再去工作里试试。想换条职业路线也可以，已经学会的东西不会白费。',
      '先練一點，再去工作裡試試。想換條職業路線也可以，已經學會的東西不會白費。',
      'Practica un poco y pruébalo en un turno. Puedes explorar otra profesión sin perder lo que ya has aprendido.',
    ),
    familiar: text(
      'New York has plenty of people asking, “What do you do?” I like the other question: “What would you like to try next?”',
      '纽约有太多人一开口就问“你是做什么的”。我更喜欢问：“接下来，你想试试什么？”',
      '紐約有太多人一開口就問「你是做什麼的」。我更喜歡問：「接下來，你想試試什麼？」',
      'En Nueva York se oye mucho eso de «¿A qué te dedicas?». A mí me gusta otra pregunta: «¿Qué te gustaría probar ahora?».',
    ),
  },
  rosa: {
    intro: text(
      "I'm Rosa, your NPC housing helper. Your basic room stays yours. Take a breath; you have somewhere to come home to.",
      '我是 Rosa，你的 NPC 住房助手。基础房间一直为你保留。先松口气，你在这里有个可以回去的家了。',
      '我是 Rosa，你的 NPC 住房助手。基礎房間一直為你保留。先鬆口氣，你在這裡有個可以回去的家了。',
      'Soy Rosa, tu asesora de vivienda NPC. Tu habitación básica seguirá siendo tuya. Respira: ya tienes un lugar al que volver.',
    ),
    progress: text(
      'A lamp, a little green, somewhere to sit. A room can start feeling like home long before it is full.',
      '一盏灯、一抹绿意、一个能坐下的地方。房间不用摆得满满当当，也能慢慢有家的样子。',
      '一盞燈、一抹綠意、一個能坐下的地方。房間不用擺得滿滿當當，也能慢慢有家的樣子。',
      'Una lámpara, una planta, un sitio donde sentarse. Una habitación puede sentirse como un hogar mucho antes de estar llena.',
    ),
    familiar: text(
      'Let the city keep its grand skyline. A little room with things you like has a view of its own.',
      '壮观的天际线就留给城市吧。小房间里放着自己喜欢的东西，也是一种好风景。',
      '壯觀的天際線就留給城市吧。小房間裡放著自己喜歡的東西，也是一種好風景。',
      'Que la ciudad se quede con su gran horizonte. Una habitación pequeña con cosas que te gustan también tiene su encanto.',
    ),
  },
  jordan: {
    intro: text(
      "Hey, I'm Jordan, your NPC neighbor. This patch of riverfront is my favorite place to slow down. Have a look at the neighborhood project while you're here.",
      '嘿，我是 Jordan，你的 NPC 邻居。我最喜欢来这段河边放慢脚步。既然来了，也看看街区的公共项目吧。',
      '嘿，我是 Jordan，你的 NPC 鄰居。我最喜歡來這段河邊放慢腳步。既然來了，也看看街區的公共專案吧。',
      'Hola, soy Jordan, tu vecino NPC. Este rincón junto al río es mi favorito para bajar el ritmo. Ya que estás, échale un vistazo al proyecto del barrio.',
    ),
    progress: text(
      "You don't need to bring a whole group to help out. A small contribution is still a real part of the project.",
      '不用凑齐一群人才能帮忙。小小的一份贡献，也是这个项目实实在在的一部分。',
      '不用湊齊一群人才能幫忙。小小的一份貢獻，也是這個專案實實在在的一部分。',
      'No hace falta reunir a un grupo para ayudar. Una aportación pequeña también es una parte real del proyecto.',
    ),
    familiar: text(
      'Back by the river again? Good choice. All those towers across the water, and this little bit of green right here.',
      '又来河边啦？来对地方了。对岸是高楼，这边有这一小片绿地，刚刚好。',
      '又來河邊啦？來對地方了。對岸是高樓，這邊有這一小片綠地，剛剛好。',
      '¿Otra vez por el río? Buena elección. Las torres al otro lado del agua y este pedacito de verde aquí mismo.',
    ),
  },
  casey: {
    intro: text(
      "I'm Casey, the NPC civic coordinator. This hall is for our fictional in-game city. I can point you toward its rules, projects, and election information.",
      '我是 Casey，NPC 公共事务协调员。这里处理的是虚构游戏城市的事务，可以查看规则、公共项目和游戏内选举信息。',
      '我是 Casey，NPC 公共事務協調員。這裡處理的是虛構遊戲城市的事務，可以查看規則、公共專案和遊戲內選舉資訊。',
      'Soy Casey, el coordinador cívico NPC. Este edificio pertenece a nuestra ciudad ficticia del juego. Aquí puedes consultar reglas, proyectos y elecciones del juego.',
    ),
    progress: text(
      'An empty office does not mean an empty city. Published caretaker rules keep basic services going. NPCs do not cast votes.',
      '职位暂时空着，城市也照常运转。基础服务按公开的托管规则继续，NPC 不会投票。',
      '職位暫時空著，城市也照常運轉。基礎服務按公開的託管規則繼續，NPC 不會投票。',
      'Un cargo vacante no deja la ciudad parada. Las reglas de gestión provisional mantienen los servicios básicos. Los NPC no votan.',
    ),
    familiar: text(
      'The café counter and the park bench are part of city life, too. You can make a place for yourself here without running for office.',
      '咖啡馆的柜台、公园的长椅，也都是城市生活的一部分。不参选，你也能在这里拥有自己的位置。',
      '咖啡館的櫃檯、公園的長椅，也都是城市生活的一部分。不參選，你也能在這裡擁有自己的位置。',
      'El mostrador del café y el banco del parque también son vida de ciudad. Puedes encontrar tu sitio aquí sin presentarte a ningún cargo.',
    ),
  },
};

/** Backgrounds change the story voice, never starting wealth or civic rights. */
export const backgroundStoryCopy = {
  graduate: {
    welcome: text(
      'One chapter finished, a New York chapter just beginning. For now, there is a room to settle into and a first shift to try.',
      '上一章刚翻完，纽约这一章才开始。眼下，先把小房间安顿好，再试试第一份工作。',
      '上一章剛翻完，紐約這一章才開始。眼下，先把小房間安頓好，再試試第一份工作。',
      'Se cierra una etapa y empieza otra en Nueva York. Por ahora, toca acomodarte en tu habitación y probar un primer turno.',
    ),
    firstPaycheck: text(
      'Your first city paycheck. A little less wondering what comes after graduation, a little more having something of your own underway.',
      '在这座城市领到第一份工资了。“毕业以后怎么办”的问号少了一点，属于自己的生活往前走了一点。',
      '在這座城市領到第一份薪水了。「畢業以後怎麼辦」的問號少了一點，屬於自己的生活往前走了一點。',
      'Tu primer sueldo en la ciudad. Un poco menos de «¿y ahora qué, después de graduarme?» y un poco más de empezar algo propio.',
    ),
  },
  switcher: {
    welcome: text(
      'A new direction, in a city full of them. Try a shift, find a corner you like, and let this part of your story take shape.',
      '换个方向，来到一座处处都有岔路的城市。先试一班工作，找个喜欢的街角，让新的生活慢慢成形。',
      '換個方向，來到一座處處都有岔路的城市。先試一班工作，找個喜歡的街角，讓新的生活慢慢成形。',
      'Un nuevo rumbo en una ciudad llena de caminos. Prueba un turno, encuentra un rincón que te guste y deja que esta etapa vaya tomando forma.',
    ),
    firstPaycheck: text(
      'The first paycheck on a different path. It does not have to explain the whole career change. Today, it is a beginning you can point to.',
      '新路上的第一份工资。不用拿它证明整个转行决定，今天，你已经有了一个实实在在的开始。',
      '新路上的第一份薪水。不用拿它證明整個轉行決定，今天，你已經有了一個實實在在的開始。',
      'El primer sueldo de un camino distinto. No tiene que justificar todo el cambio de carrera. Hoy ya tienes un comienzo concreto.',
    ),
  },
  founder: {
    welcome: text(
      'Maybe one day there will be a café with your name behind it. Today starts smaller: a room, a shift, and a neighborhood to get to know.',
      '也许以后，会有一家由你做主的咖啡店。今天先从小处开始：一间房、一次工作，还有一个等你熟悉的街区。',
      '也許以後，會有一家由你做主的咖啡店。今天先從小處開始：一間房、一次工作，還有一個等你熟悉的街區。',
      'Quizá algún día abras tu propia cafetería. Hoy todo empieza más pequeño: una habitación, un turno y un barrio por conocer.',
    ),
    firstPaycheck: text(
      'Your first paycheck. Alongside the big café ideas, there are ordinary working days like this one. You have something earned to build on.',
      '第一份工资到手了。关于咖啡店的大想法之外，也有这样踏踏实实上班的日子。这一次，你攒下了自己的劳动所得。',
      '第一份薪水到手了。關於咖啡店的大想法之外，也有這樣踏踏實實上班的日子。這一次，你存下了自己的勞動所得。',
      'Tu primer sueldo. Junto a las grandes ideas para el café también hay jornadas de trabajo como esta. Ya tienes algo ganado con lo que seguir.',
    ),
  },
};

/** Show only after the server confirms the matching existing goal is complete. */
export const chapterNotes = {
  'first-shift': text(
    'First shift finished, first paycheck earned. Your New York story has something more than a starting point now.',
    '第一班完成，第一份工资到手。你的纽约故事，已经不只有一个开头了。',
    '第一班完成，第一份薪水到手。你的紐約故事，已經不只有一個開頭了。',
    'Primer turno terminado, primer sueldo ganado. Tu historia en Nueva York ya tiene algo más que un punto de partida.',
  ),
  'settle-in': text(
    'A meal and a proper rest. For a moment, this busy city can wait while you settle into your own rhythm.',
    '吃过饭，也好好休息了。这座忙碌的城市可以先等等，你正慢慢找到自己的节奏。',
    '吃過飯，也好好休息了。這座忙碌的城市可以先等等，你正慢慢找到自己的節奏。',
    'Una comida y un buen descanso. Por un momento, esta ciudad tan ajetreada puede esperar mientras encuentras tu ritmo.',
  ),
  'make-it-yours': text(
    'One piece of furniture, one personal touch. The room is starting to feel less like an address and more like yours.',
    '添了一件家具，也添了一点自己的喜好。这间房开始不只是一个地址，而是你的家了。',
    '添了一件家具，也添了一點自己的喜好。這間房開始不只是一個地址，而是你的家了。',
    'Un mueble, un toque personal. La habitación empieza a ser menos una dirección y más tu propio lugar.',
  ),
  'city-explorer': text(
    'Three places visited. The map is turning into little corners you recognize, one stop at a time.',
    '去过三个地方了。地图上的标记，正一站一站变成你认得的街角。',
    '去過三個地方了。地圖上的標記，正一站一站變成你認得的街角。',
    'Tres lugares visitados. Las marcas del mapa se van convirtiendo en rincones que reconoces, parada a parada.',
  ),
  'good-neighbor': text(
    'You lent a hand to the neighborhood. A small everyday act, and a real part of your life here.',
    '你为街区帮上了忙。只是一件日常小事，也已经是你在这里生活的一部分。',
    '你為街區幫上了忙。只是一件日常小事，也已經是你在這裡生活的一部分。',
    'Has echado una mano al barrio. Un pequeño gesto cotidiano que ya forma parte de tu vida aquí.',
  ),
  'next-chapter': text(
    'Level two in a career. Something that was new a little while ago is becoming something you know how to do.',
    '一条职业路线升到二级了。前些时候还陌生的事，正慢慢变成你的拿手活。',
    '一條職業路線升到二級了。前些時候還陌生的事，正慢慢變成你的拿手活。',
    'Nivel dos en una carrera. Lo que hace poco era nuevo empieza a convertirse en algo que sabes hacer.',
  ),
  'open-door': text(
    'Your own café has a name and a place in the neighborhood. The opening is yours; the next chapter is still unwritten.',
    '你的咖啡店有了名字，也在街区里有了位置。开店这一页属于你，后面的故事还等着你来写。',
    '你的咖啡店有了名字，也在街區裡有了位置。開店這一頁屬於你，後面的故事還等著你來寫。',
    'Tu cafetería ya tiene nombre y un lugar en el barrio. La apertura es tuya; el próximo capítulo aún está por escribir.',
  ),
};
